import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Store the session secret in memory (also persisted via context/localStorage)
let sessionSecret = null;

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authorization header to all requests if we have a session secret
client.interceptors.request.use((config) => {
  if (sessionSecret) {
    config.headers.Authorization = `Bearer ${sessionSecret}`;
  }
  return config;
});

/**
 * Set the session secret for API authentication.
 * This should be called after upload with the session_secret from the response.
 */
export const setSessionSecret = (secret) => {
  sessionSecret = secret;
};

/**
 * Clear the session secret (e.g., on logout or session reset).
 */
export const clearSessionSecret = () => {
  sessionSecret = null;
};

/**
 * Get current session secret (for persistence).
 */
export const getSessionSecret = () => sessionSecret;

// API functions
export const uploadChat = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  // Note: upload doesn't require auth - it's how we get the session secret
  const response = await client.post('/api/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  // Automatically set the session secret from the upload response
  if (response.data.session_secret) {
    setSessionSecret(response.data.session_secret);
  }
  
  return response.data;
};

export const getSession = async (sessionId) => {
  const response = await client.get(`/api/session/${sessionId}`);
  return response.data;
};

export const getStatistics = async (sessionId) => {
  const response = await client.get(`/api/session/${sessionId}/statistics`);
  return response.data;
};

export const getAvailableYears = async (sessionId) => {
  const response = await client.get(`/api/session/${sessionId}/years`);
  return response.data;
};

export const startBuild = async (sessionId, config) => {
  const response = await client.post(`/api/session/${sessionId}/build`, { config });
  return response.data;
};

export const getBuildStatus = async (sessionId) => {
  const response = await client.get(`/api/session/${sessionId}/status`);
  return response.data;
};

export const deleteSession = async (sessionId) => {
  const response = await client.delete(`/api/session/${sessionId}`);
  return response.data;
};

// Cover photo functions
export const uploadCover = async (sessionId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await client.post(`/api/session/${sessionId}/cover`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Fetch the cover image and return a blob URL.
 * This is needed because <img src="..."> doesn't support Authorization headers.
 */
export const fetchCoverAsBlob = async (sessionId) => {
  const response = await client.get(`/api/session/${sessionId}/cover`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data);
};

/**
 * @deprecated Use fetchCoverAsBlob instead - direct URLs don't include auth.
 */
export const getCoverUrl = (sessionId) => {
  console.warn('getCoverUrl is deprecated - URLs do not include auth. Use fetchCoverAsBlob instead.');
  return `${API_BASE_URL}/api/session/${sessionId}/cover`;
};

// Cover style generation functions
/**
 * Generate a styled version of the cover image.
 * @param {string} sessionId - Session ID
 * @param {string} style - Style to apply: 'cartoon', 'anime', 'storybook', 'vintage_comic', 'cyber'
 * @returns {Promise<{session_id: string, style: string, generations_remaining: number, message: string}>}
 */
export const stylizeCover = async (sessionId, style) => {
  const response = await client.post(`/api/session/${sessionId}/cover/stylize`, {
    style,
  });
  return response.data;
};

/**
 * Fetch the styled cover image and return a blob URL along with the style.
 * This is needed because <img src="..."> doesn't support Authorization headers.
 * @returns {Promise<{url: string, style: string}>}
 */
export const fetchStyledCoverAsBlob = async (sessionId) => {
  const response = await client.get(`/api/session/${sessionId}/cover/styled`, {
    responseType: 'blob',
  });
  const style = response.headers['x-cover-style'] || null;
  return {
    url: URL.createObjectURL(response.data),
    style,
  };
};

/**
 * Get the number of cover style generations used and remaining.
 * @returns {Promise<{session_id: string, generations_used: number, generations_remaining: number, max_generations: number}>}
 */
export const getCoverGenerationsRemaining = async (sessionId) => {
  const response = await client.get(`/api/session/${sessionId}/cover/generations`);
  return response.data;
};

// Book metadata functions
export const saveBookMetadata = async (sessionId, { title, subtitle, year, dedication }) => {
  const formData = new FormData();
  formData.append('title', title);
  if (subtitle) formData.append('subtitle', subtitle);
  if (year) formData.append('year', year);
  if (dedication) formData.append('dedication', dedication);
  
  const response = await client.post(`/api/session/${sessionId}/book-metadata`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getBookMetadata = async (sessionId) => {
  const response = await client.get(`/api/session/${sessionId}/book-metadata`);
  return response.data;
};

// PDF generation functions
export const generatePDF = async (sessionId, config, stories, chapters = [], characters = []) => {
  const response = await client.post(`/api/session/${sessionId}/generate-pdf`, {
    config,
    stories,
    chapters,  // Full chapter list for TOC (non-preview chapters greyed out)
    characters,  // Character bios for the cast of characters page
  });
  return response.data;
};

/**
 * Fetch the PDF and trigger a download.
 * This is needed because direct URLs don't include auth headers.
 */
export const downloadPDF = async (sessionId, filename = 'yearbook.pdf') => {
  const response = await client.get(`/api/session/${sessionId}/download-pdf`, {
    responseType: 'blob',
  });
  
  // Create a blob URL and trigger download
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the blob URL
  URL.revokeObjectURL(url);
};

/**
 * @deprecated Use downloadPDF instead - direct URLs don't include auth.
 */
export const getPDFDownloadUrl = (sessionId) => {
  console.warn('getPDFDownloadUrl is deprecated - URLs do not include auth. Use downloadPDF instead.');
  return `${API_BASE_URL}/api/session/${sessionId}/download-pdf`;
};

// Participant name mapping functions
export const getParticipants = async (sessionId) => {
  const response = await client.get(`/api/session/${sessionId}/participants`);
  return response.data;
};

export const getNameMappings = async (sessionId) => {
  const response = await client.get(`/api/session/${sessionId}/name-mappings`);
  return response.data;
};

export const saveNameMappings = async (sessionId, mappings) => {
  const response = await client.post(`/api/session/${sessionId}/name-mappings`, {
    mappings,
  });
  return response.data;
};

export const deleteNameMappings = async (sessionId) => {
  const response = await client.delete(`/api/session/${sessionId}/name-mappings`);
  return response.data;
};

// ============================================================================
// PAYMENT FUNCTIONS
// ============================================================================

/**
 * Get current pricing information for the digital book.
 */
export const getPrice = async () => {
  const response = await client.get('/api/payment/price');
  return response.data;
};

/**
 * Create a PayPal order for purchasing the full book.
 * @param {string} sessionId - Session ID
 * @param {string} bookTitle - Title of the book
 * @returns {Promise<{order_id: string, status: string}>}
 */
export const createOrder = async (sessionId, bookTitle) => {
  const response = await client.post(`/api/session/${sessionId}/create-order`, {
    book_title: bookTitle,
  });
  return response.data;
};

/**
 * Capture a PayPal order after user approval.
 * @param {string} sessionId - Session ID
 * @param {string} orderId - PayPal order ID from createOrder
 * @param {string} eventId - Optional event ID for Meta CAPI deduplication
 * @returns {Promise<{order_id: string, status: string, message: string}>}
 */
export const captureOrder = async (sessionId, orderId, eventId = null) => {
  const payload = { order_id: orderId };
  if (eventId) {
    payload.event_id = eventId;
  }
  const response = await client.post(`/api/session/${sessionId}/capture-order`, payload);
  return response.data;
};

/**
 * Check if the user has purchased the full book.
 * @param {string} sessionId - Session ID
 * @returns {Promise<{purchased: boolean, order_id?: string, purchased_at?: string}>}
 */
export const getPurchaseStatus = async (sessionId) => {
  const response = await client.get(`/api/session/${sessionId}/purchase-status`);
  return response.data;
};

/**
 * Generate the full book PDF after purchase.
 * @param {string} sessionId - Session ID
 * @param {object} config - PDF config (title, subtitle, dedication, yearbook_type)
 * @param {Array} stories - All story data
 * @param {Array} chapters - All chapters
 * @param {Array} characters - Character bios
 * @returns {Promise<{session_id: string, pdf_filename: string, page_count: number, message: string}>}
 */
export const generateFullPDF = async (sessionId, config, stories, chapters = [], characters = []) => {
  const response = await client.post(`/api/session/${sessionId}/generate-full-pdf`, {
    config,
    stories,
    chapters,
    characters,
  });
  return response.data;
};

/**
 * Download the full book PDF after purchase.
 * The PDF is decrypted server-side and streamed to the user.
 * @param {string} sessionId - Session ID
 * @param {string} filename - Desired filename for download
 */
export const downloadFullPDF = async (sessionId, filename = 'yearbook_full.pdf') => {
  const response = await client.get(`/api/session/${sessionId}/download-full-pdf`, {
    responseType: 'blob',
  });
  
  // Create a blob URL and trigger download
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the blob URL
  URL.revokeObjectURL(url);
};

export default client;
